import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import useAgencySelector from '@hooks/useAgencySelector'
import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as templatePageActions from '@redux/modules/templatePage'

import defaultFormState, { requiredFields } from '@models/templatePage'

import PageContext from '@contexts/pageContext'

import type { BrandModel, TemplatePageModel, TemplatePageRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (templatePage: Partial<TemplatePageModel>, options?: GenerateUrlsOptions) => {
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    templatePagesIndexUrl: `#/${prefixRoute}/templateGallery?hidden=false`,
    unassignedTemplatePagesUrl: `#/${prefixRoute}/templateGallery?hidden=true`,
  }
}

type BulkUpdatePermissionsParams = {
  dispatch: AppDispatch,
  requestOptions?: TemplatePageRequestOptions,
  templatePage: TemplatePageModel,
  permissions: {
    agency_ids: number[],
  },
}

const bulkUpdatePermissions = (params: BulkUpdatePermissionsParams) => {
  const {
    dispatch, templatePage, permissions, requestOptions,
  } = params

  const { bulkUpdatePermissions: bulkUpdatePermissionsFn } = templatePageActions

  const updatedParams = {
    id: templatePage.id,
    permissions,
  }

  return dispatch(bulkUpdatePermissionsFn(updatedParams, permissions, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTemplatePageForm(
  templatePage: Partial<TemplatePageModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const templatePageForm = useForm(
    defaultFormState,
    { entity: templatePage, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [templatePage.id, templatePage.cache_key],
  )

  return {
    ...templatePageForm,
  }
}

export const useRelations = (templatePage: TemplatePageModel) => {
  const { brand_id } = templatePage

  const { brands } = useSelector(reduxState => reduxState.entities)

  const brand: Partial<BrandModel> = brand_id && brands[brand_id] ? brands[brand_id] : {}

  return {
    brand,
  }
}

type UseTemplatePageOptions = {
  generateUrlsOptions?: GenerateUrlsOptions,
  performHttpRequests?: boolean,
  requestOptions?: TemplatePageRequestOptions,
}

function useTemplatePage(initEntity: Partial<TemplatePageModel> = {}, options: UseTemplatePageOptions = {}) {
  const { generateUrlsOptions, performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: templatePage }: { entity: TemplatePageModel } = useLatestEntity(initEntity, 'templatePages')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { brand } = useRelations(templatePage)

  useReduxAction(
    'templatePages',
    'loadTemplatePage',
    {
      entityId: templatePage.id,
      ...requestOptions,
    },
    [templatePage.id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(templatePage.id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const { providers } = useSelector(reduxState => reduxState.entities)

  const {
    loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.templatePages)

  const enabledAgencies = digObject(templatePage, 'permission_ids.agencies', [])

  const { selectedAgencyId } = useAgencySelector()
  const isEnabledForAgency = enabledAgencies.includes(selectedAgencyId)

  return {
    brand,
    callbacks: {
      templatePageDetails: () => launchModal({
        callbacks,
        modalKey: 'TemplatePageDetailsModal',
        payload: { templatePage },
      }),
      bulkUpdatePermissions:
        (
          permissions: {
            agency_ids: number[],
          },
          entityOptions?: TemplatePageRequestOptions,
        ) => (
          bulkUpdatePermissions({
            templatePage, permissions, dispatch, requestOptions: entityOptions,
          })
        ),
    },
    isEnabledForAgency,
    loading: loading && !loadedIds.includes(templatePage.id),
    providers,
    templatePage,
    updating,
    urls: generateUrls({}, generateUrlsOptions),
  }
}

export default useTemplatePage
